// socketStore.ts
import { create } from "zustand";
import { createSocketConnection } from "./lib/socket";
import { refreshSession } from "./http/api";
import type { MessageType, NotificationPayload } from "./types";

/** Reply from the server's order_message acknowledgement. */
export type SendAck = {
  ok: boolean;
  message?: MessageType;
  code?: string;
};

/** How long to wait for a send acknowledgement before treating it as failed. */
const SEND_ACK_TIMEOUT_MS = 15000;

/**
 * How many times to refresh the access token and re-dial after the server has
 * refused a handshake. Bounded, because a refusal a new token cannot fix (a
 * revoked session, say) would otherwise loop forever.
 */
const MAX_AUTH_RECOVERY_ATTEMPTS = 3;

type SocketStore = {
  socket: ReturnType<typeof createSocketConnection>;
  isConnected: boolean;
  /**
   * Why the socket is not connected, when the server refused it. Null while
   * healthy. Exposed so the UI can say something more useful than nothing.
   */
  connectionError: string | null;
  /**
   * Increments on every RE-connect (not the first connect). Subscribers watch
   * it to reconcile whatever they missed while the socket was down.
   */
  reconnectNonce: number;
  connect: () => void;
  disconnect: () => void;
  joinOrderChat: (orderId: string) => void;
  leaveOrderChat: (orderId: string) => void;
  /**
   * Tells the server which order's chat is actually on screen right now, so
   * it can skip pushing a chat notification to a receiver who is already
   * looking at that exact conversation. Distinct from joinOrderChat: that
   * tracks room membership for live delivery and stays joined for as long as
   * an order's page is open, which is not the same as the chat being the
   * thing in view. Pass null when it is no longer the active chat.
   */
  setActiveOrderChat: (orderId: string | null) => void;
  listenForMessages: (cb: (data: unknown) => void) => () => void;
  sendMessage: (payload: MessageType, ack?: (result: SendAck) => void) => void;
  recieveNotification: (cb: (data: unknown) => void) => () => void;
  sendNotification: (payload: NotificationPayload) => void;
};

export const useSocketStore = create<SocketStore>((set) => {
  const socket = createSocketConnection();

  /**
   * Rooms this client believes it is in.
   *
   * A reconnect produces a NEW socket on the server, and that socket belongs to
   * no rooms -- socket.io does not restore them. Nothing re-joined them, so
   * after any dropped connection live messages silently stopped arriving until
   * the page was reloaded. Tracking them here lets the connect handler restore
   * every room automatically.
   */
  const joinedOrderIds = new Set<string>();
  /**
   * The order id last passed to setActiveOrderChat, or null. Re-sent on every
   * reconnect for the same reason joinedOrderIds is: a reconnect is a NEW
   * socket on the server, which starts with no active chat recorded for it.
   */
  let activeOrderChatId: string | null = null;
  let hasConnectedBefore = false;

  /** Refused handshakes recovered from since the last successful connect. */
  let authRecoveryAttempts = 0;
  let revivalTimer: ReturnType<typeof setTimeout> | null = null;
  /** Guards against a burst of failed sends firing overlapping revivals. */
  let revivalInFlight = false;
  /** True between disconnect() and the next connect(), so revival stays off. */
  let closedDeliberately = false;

  /**
   * Bring back a socket the server refused, by minting a fresh access token.
   *
   * The handshake authenticates with the access token cookie (15 minutes) and
   * is checked exactly once, at connect time. An established socket therefore
   * outlives its own token -- but the moment anything drops it (sleep, a proxy
   * idle timeout, an API restart) the reconnect handshake presents the expired
   * cookie and the server rejects it in a middleware.
   *
   * Socket.IO does not retry a middleware rejection. `socket.active` goes
   * false and the client stops for good, silently: nothing here listened for
   * connect_error, so the socket simply stayed dead for the rest of the page
   * session while the UI carried on as if it were fine.
   *
   * The visible symptom was chat. Every send buffered into a socket that could
   * never flush, timed out after SEND_ACK_TIMEOUT_MS and reported "not sent";
   * retry re-sent into the same dead socket and failed identically, which made
   * retry itself look broken. Only a reload recovered it -- because the
   * reload's HTTP traffic refreshed the cookie as a side effect.
   */
  const reviveSocket = async (reason: string) => {
    if (closedDeliberately || revivalInFlight) return;

    if (authRecoveryAttempts >= MAX_AUTH_RECOVERY_ATTEMPTS) {
      console.error(
        "[socket] handshake refused repeatedly, staying offline:",
        reason
      );
      return;
    }

    revivalInFlight = true;
    try {
      const refreshed = await refreshSession();
      if (!refreshed) {
        // A refresh can fail because the network is down, which says nothing
        // about whether the token is valid. Not counted as an attempt, so a
        // recoverable outage cannot exhaust the budget and leave the socket
        // permanently offline once connectivity returns.
        console.error("[socket] token refresh failed, socket stays offline");
        return;
      }
      if (closedDeliberately) return;

      const attempt = ++authRecoveryAttempts;

      // Backed off, so a refusal a new token cannot fix is not hammered.
      if (revivalTimer) clearTimeout(revivalTimer);
      revivalTimer = setTimeout(() => {
        revivalTimer = null;
        socket.connect();
      }, attempt * 1000);
    } finally {
      revivalInFlight = false;
    }
  };

  const handleConnectError = (err: Error) => {
    console.error("[socket] connect error:", err.message);
    set({ isConnected: false, connectionError: err.message });

    // `active` is Socket.IO's own answer to "will I reconnect by myself?".
    // True for a transport-level failure -- its backoff is better at that than
    // anything here. False only when the server refused us, which is the case
    // that needs a new token.
    if (socket.active) return;

    void reviveSocket(err.message);
  };

  const handleConnect = () => {
    console.log("✅ Socket connected:", socket.id);
    authRecoveryAttempts = 0;
    set({ isConnected: true, connectionError: null });

    // Restore rooms before anything else, so messages sent from here on arrive.
    joinedOrderIds.forEach((orderId) => {
      socket.emit("join_order_chat", { orderId });
    });

    // Same restore, for the one active chat (if any) this socket had marked.
    if (activeOrderChatId) {
      socket.emit("set_active_order_chat", { orderId: activeOrderChatId });
    }

    // Only on a RE-connect: the first connect has no gap to recover.
    if (hasConnectedBefore) {
      set((s) => ({ reconnectNonce: s.reconnectNonce + 1 }));
    }
    hasConnectedBefore = true;
  };

  const handleJoined = (room: string) => {
    console.log(`🎉 Joined room: ${room}`);
  };

  const handleJoinError = (msg: string) => {
    console.error("Join Error:", msg);
  };

  const handleDisconnect = (reason: string) => {
    console.log("❌ Socket disconnected:", reason);
    set({ isConnected: false });

    // "io server disconnect" is the server closing us on purpose -- it does
    // that when the access token behind the handshake expires. Socket.IO does
    // not reconnect from a deliberate server close any more than it does from
    // a refused handshake, so the same revival path applies. Every other
    // reason is a transport failure Socket.IO retries by itself.
    if (reason === "io server disconnect") {
      void reviveSocket(reason);
    }
  };

  return {
    socket,
    isConnected: false,
    connectionError: null,
    reconnectNonce: 0,

    connect: () => {
      closedDeliberately = false;
      // Remove any previous listeners before adding new ones to prevent
      // listener accumulation across reconnections and React StrictMode double-renders.
      // Remove only OUR handlers by reference. A bare socket.off("connect")
      // would also strip the reconnect/re-join handler, which is the one thing
      // that must survive.
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("joined", handleJoined);
      socket.off("error", handleJoinError);
      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      socket.on("connect_error", handleConnectError);
      socket.on("joined", handleJoined);
      socket.on("error", handleJoinError);

      if (socket.connected) {
        // Already connected — the "connect" event won't fire again,
        // so we set the flag synchronously here.
        set({ isConnected: true });
        hasConnectedBefore = true;
      } else {
        socket.connect();
      }
    },

    joinOrderChat: (orderId: string) => {
      joinedOrderIds.add(orderId);
      socket.emit("join_order_chat", { orderId });
    },
    leaveOrderChat: (orderId: string) => {
      joinedOrderIds.delete(orderId);
      socket.emit("leave_order_chat", { orderId });
    },

    setActiveOrderChat: (orderId: string | null) => {
      activeOrderChatId = orderId;
      socket.emit("set_active_order_chat", { orderId });
    },

    listenForMessages: (cb) => {
      socket.on("order_message", cb);
      return () => socket.off("order_message", cb);
    },

    sendMessage: (payload: MessageType, ack) => {
      // No callback -> fire and forget, exactly as before.
      if (!ack) {
        socket.emit("order_message", payload);
        return;
      }

      // A socket that is neither connected nor reconnecting will never flush
      // this packet. Emitting anyway buffers it, waits out the full ack
      // timeout and then reports TIMEOUT -- 15 seconds to learn nothing, and
      // the same 15 seconds again on every retry, which is what made retry
      // look broken. Fail immediately with a code the UI can explain, and try
      // to revive the socket so the next retry has something to send on.
      if (!socket.connected && !socket.active) {
        void reviveSocket("send attempted on a closed socket");
        ack({ ok: false, code: "DISCONNECTED" });
        return;
      }

      // With a callback the server replies { ok, message } or { ok:false, code }.
      // The timeout matters: without it a send that never gets a reply (socket
      // dropped mid-flight) would sit in "sending" forever, which is the silent
      // failure that made messages look like they had vanished.
      socket
        .timeout(SEND_ACK_TIMEOUT_MS)
        .emit("order_message", payload, (err: unknown, result: SendAck | undefined) => {
          if (err) return ack({ ok: false, code: "TIMEOUT" });
          ack(result ?? { ok: false, code: "NO_RESPONSE" });
        });
    },

    // Notifications
    sendNotification: (payload: NotificationPayload) => {
      socket.emit("send_notification", payload);
    },

    recieveNotification: (cb) => {
      socket.on("receive_notification", cb);
      return () => socket.off("receive_notification", cb);
    },

    disconnect: () => {
      // Deliberate: stop the revival machinery from dialling back in behind
      // the caller's back on logout or unmount.
      closedDeliberately = true;
      if (revivalTimer) {
        clearTimeout(revivalTimer);
        revivalTimer = null;
      }
      socket.disconnect();
      set({ isConnected: false, connectionError: null });
    },
  };
});
